import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Input, Select, Switch } from "@discordforge/ui";
import { settingsUpdate } from "../api";
import { useMail } from "../MailApp";

interface GeneralDraft {
    messagesPerPage: string;
    autoLogout: string;
    desktopNotifications: boolean;
    viewImages: string;
    viewHtml: boolean;
}

function toDraft(loginData: ReturnType<typeof useMail>["loginData"]): GeneralDraft {
    return {
        messagesPerPage: String(loginData.MessagesPerPage ?? 20),
        autoLogout: String(loginData.AutoLogout ?? 30),
        desktopNotifications: !!loginData.DesktopNotifications,
        viewImages: loginData.ViewImages ?? "ask",
        viewHtml: !!loginData.ViewHTML,
    };
}

type SaveState = "idle" | "saving" | "saved" | "error";

/* Row wrapper: label block left, control right — settings-table style. */
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-8 border-b border-white/[0.06] py-4">
            <div className="min-w-0">
                <div className="text-sm font-bold text-zinc-200">{label}</div>
                {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-2">{children}</div>
        </div>
    );
}

export function GeneralTab() {
    const { loginData } = useMail();
    const [draft, setDraft] = useState<GeneralDraft>(() => toDraft(loginData));
    const [saveState, setSaveState] = useState<SaveState>("idle");

    // Keep the form in sync when the session data changes (e.g. fresh login).
    useEffect(() => {
        setDraft(toDraft(loginData));
    }, [loginData]);

    const set = <K extends keyof GeneralDraft>(key: K, value: GeneralDraft[K]) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
        setSaveState("idle");
    };

    const save = async (patch: Record<string, string | number | boolean>) => {
        setSaveState("saving");
        try {
            await settingsUpdate(patch);
            setSaveState("saved");
            setTimeout(() => setSaveState("idle"), 2000);
        } catch {
            setSaveState("error");
        }
    };

    const saveNumber = (key: "messagesPerPage" | "autoLogout", value: string) => {
        const n = parseInt(value, 10);
        if (Number.isNaN(n) || n <= 0) return;
        save({ [key === "messagesPerPage" ? "MessagesPerPage" : "AutoLogout"]: n });
    };

    return (
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] px-6">
            <Row
                label="Messages per page"
                hint="How many messages the list loads at once (10–100)."
            >
                <Input
                    type="number"
                    min={10}
                    max={100}
                    value={draft.messagesPerPage}
                    onChange={(e) => set("messagesPerPage", e.target.value)}
                    onBlur={() => saveNumber("messagesPerPage", draft.messagesPerPage)}
                    className="w-24 text-center"
                    aria-label="Messages per page"
                />
            </Row>

            <Row
                label="Auto-logout"
                hint="Sign out automatically after this many idle minutes (0 = never)."
            >
                <Input
                    type="number"
                    min={0}
                    max={720}
                    value={draft.autoLogout}
                    onChange={(e) => set("autoLogout", e.target.value)}
                    onBlur={() => saveNumber("autoLogout", draft.autoLogout)}
                    className="w-24 text-center"
                    aria-label="Auto-logout minutes"
                />
            </Row>

            <Row
                label="Desktop notifications"
                hint="Show a system notification when new mail arrives."
            >
                <Switch
                    on={draft.desktopNotifications}
                    onToggle={() => {
                        const next = !draft.desktopNotifications;
                        set("desktopNotifications", next);
                        save({ DesktopNotifications: next });
                    }}
                    aria-label="Desktop notifications"
                />
            </Row>

            <Row label="View images in messages" hint="When to load remote images (privacy).">
                <Select
                    value={draft.viewImages}
                    onChange={(value) => {
                        set("viewImages", value);
                        save({ ViewImages: value });
                    }}
                    options={[
                        { value: "ask", label: "Ask" },
                        { value: "always", label: "Always" },
                        { value: "never", label: "Never" },
                    ]}
                    className="w-44"
                />
            </Row>

            <Row label="HTML messages" hint="Render messages as HTML instead of plain text.">
                <Switch
                    on={draft.viewHtml}
                    onToggle={() => {
                        const next = !draft.viewHtml;
                        set("viewHtml", next);
                        save({ ViewHTML: next });
                    }}
                    aria-label="HTML messages"
                />
            </Row>

            {/* Save status indicator */}
            <div className="flex h-10 items-center justify-end" aria-live="polite">
                {saveState === "saving" && (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400">
                        <Loader2 size={13} className="animate-spin" /> Saving…
                    </span>
                )}
                {saveState === "saved" && (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                        <Check size={13} /> Saved
                    </span>
                )}
                {saveState === "error" && (
                    <span className="text-xs font-semibold text-red-400">Failed to save — try again</span>
                )}
            </div>
        </section>
    );
}
